import { PROJECT_NAMESPACE } from "@/i18n/messages";

/**
 * Look up a label the tenant authored in their CMS, by its raw key.
 *
 * This replaces the legacy `useDynamicTranslation().t('project:key')`. The keys
 * are not known at build time — they're POI field names, block titles and table
 * column names out of the tenant's Squidex config — so `useTranslations()` can't
 * type them, and a missing one must render as nothing rather than as an error
 * placeholder.
 *
 * The lookup walks the merged catalog directly instead of going through
 * `t()`/`t.has()` for two reasons: a key that resolves to a *namespace* rather
 * than a string would make `t()` log an error and render the key path, and the
 * tenant's strings are plain text, never ICU — the legacy dictionary was a flat
 * `Record<string, string>` and applying ICU parsing to it would break any label
 * containing a brace or an apostrophe.
 *
 * Tenant strings are deep-merged over the app catalog (see `tenantCatalog` in `src/i18n/messages.ts`),
 * so a tenant-authored key always wins. A CMS field whose name collides with an
 * app key the tenant did *not* override — a field literally named `share` — would
 * resolve to the app string; legacy resolved it to nothing. Both are wrong in the
 * same harmless way, and the merge order is what makes overrides work everywhere
 * else in the app.
 */
export type ProjectText = (
  key: string | null | undefined,
  fallback?: string
) => string;

/** Walk a dotted path, returning the string it lands on or undefined. */
function resolve(root: unknown, key: string): string | undefined {
  let node = root;
  for (const segment of key.split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === "string" ? node : undefined;
}

/**
 * The lookup over an already-merged catalog. Outside the hook's client module
 * for the POI Page, which renders on the server without React's client tree.
 */
export function projectText(messages: Record<string, unknown>): ProjectText {
  return (key, fallback = "") => {
    if (!key) return fallback;

    // The tenant's own dictionary first: a key the merge had to drop — one
    // whose first segment collides with an app label — only exists here.
    return (
      resolve(messages[PROJECT_NAMESPACE], key) ??
      resolve(messages, key) ??
      fallback
    );
  };
}
